import type { RatingCategory } from '../types/review';
import {
  computeOverallRating,
  STANDARD_RATING_CATEGORIES,
  type StandardRatingCategory,
} from '../utils/ratingScore';
import { getAffiliateSlugFromReviewSlug, type AffiliateSlug } from '../lib/affiliateLinks';

/**
 * The overall Heaven Girlfriend ranking. This list is the only source of
 * overall rank, score, and short summary. Position in the array is the rank,
 * and the score is computed from the category scores. Homepage, /reviews,
 * /best-ai-girlfriend, review meta, CTAs, schema, and llms.txt all read it.
 *
 * To re-rank: move an entry and adjust its category scores so overall scores
 * stay in descending order (ranking tests enforce this). Keep summaries free
 * of rank claims like "#1" so they stay true after a re-rank.
 *
 * First-look reviews and category besticles are not on this list.
 */
export type PlatformRankingEntry = {
  slug: string;
  name: string;
  /** One short blurb for cards, quick answers, and CTAs. */
  summary: string;
  lastTested?: string;
  categoryScores: Record<StandardRatingCategory, number>;
};

export type RankedPlatform = Omit<PlatformRankingEntry, 'categoryScores'> & {
  /** Affiliate key, e.g. 'candy-ai' for 'candy-ai-review'. */
  key: AffiliateSlug;
  rank: number;
  rating: number;
  categories: RatingCategory[];
};

export const RANKING_LAST_VERIFIED = '2026-09-09';
export const RANKING_LAST_VERIFIED_LABEL = 'September 9, 2026';

export const PLATFORM_RANKING: PlatformRankingEntry[] = [
  {
    slug: 'candy-ai-review',
    name: 'Candy AI',
    summary:
      'Candy AI is an all-around AI girlfriend platform known for realistic AI chat, top-tier character customization, high-quality image generation, mature experiences, and advanced generative tools that deliver immersive fantasy roleplay.',
    lastTested: '2026-09-09',
    categoryScores: {
      'Chat Quality': 4.8,
      'Image Quality': 4.8,
      Features: 4.6,
      'Generative Tools': 4.6,
      'User Experience': 4.6,
      'Value/Money': 4.8,
      Characters: 3.9,
      Customization: 4.6,
      'Advanced Content': 4.7,
      'Free Trial': 4.4,
    },
  },
  {
    slug: 'ourdream-ai-review',
    name: 'OurDream AI',
    summary:
      'OurDream AI is a ranked Heaven Girlfriend companion platform in 2026, combining standout visuals, narrative-driven chats, generative tools, and strong customization for a premium companion experience.',
    lastTested: '2026-02-08',
    categoryScores: {
      'Chat Quality': 4.8,
      'Image Quality': 4.9,
      Features: 4.8,
      'Generative Tools': 4.9,
      'User Experience': 4.6,
      'Value/Money': 4.4,
      Characters: 3.6,
      Customization: 4.4,
      'Advanced Content': 4.9,
      'Free Trial': 3.7,
    },
  },
  {
    slug: 'nectar-ai-review',
    name: 'Nectar AI',
    summary:
      'Nectar AI is a ranked Heaven Girlfriend companion platform in 2026, combining powerful chat quality, seamless mode switching, robust generative tools for images and videos, and extensive customization options for deeper virtual romance.',
    lastTested: '2025-07-12',
    categoryScores: {
      'Chat Quality': 4.8,
      'Image Quality': 4.1,
      Features: 4.5,
      'Generative Tools': 4.3,
      'User Experience': 3.8,
      'Value/Money': 3.1,
      Characters: 4.3,
      Customization: 4.6,
      'Advanced Content': 5.0,
      'Free Trial': 3.2,
    },
  },
  {
    slug: 'swipey-ai',
    name: 'Swipey AI',
    summary:
      'Swipey AI is a texting-first AI girlfriend platform with voice replies and per-action heart pricing. It has a strong chat format, but image and video tools still feel unfinished.',
    lastTested: '2026-06-20',
    categoryScores: {
      'Chat Quality': 4.9,
      'Image Quality': 3.5,
      Features: 4.3,
      'Generative Tools': 3.2,
      'User Experience': 4.7,
      'Value/Money': 3.4,
      Characters: 4.4,
      Customization: 4.2,
      'Advanced Content': 4.5,
      'Free Trial': 3.8,
    },
  },
  {
    slug: 'golove-ai-review',
    name: 'GoLove AI',
    summary:
      'GoLove AI delivers a friendly, user-focused AI girlfriend experience with a good balance of customization and straightforward chats, making it a solid choice for users seeking approachable virtual companions.',
    lastTested: '2026-02-26',
    categoryScores: {
      'Chat Quality': 4.4,
      'Image Quality': 4.1,
      Features: 4.3,
      'Generative Tools': 3.8,
      'User Experience': 4.2,
      'Value/Money': 4.3,
      Characters: 4.0,
      Customization: 3.5,
      'Advanced Content': 3.9,
      'Free Trial': 4.1,
    },
  },
  {
    slug: 'darlink-ai-review',
    name: 'DarLink AI',
    summary:
      'DarLink AI offers a friendly AI companion experience with smooth interactions and easy customization for users seeking simple virtual girlfriend chats.',
    lastTested: '2026-02-08',
    categoryScores: {
      'Chat Quality': 4.3,
      'Image Quality': 3.9,
      Features: 4.1,
      'Generative Tools': 3.6,
      'User Experience': 4.2,
      'Value/Money': 3.5,
      Characters: 3.7,
      Customization: 4.1,
      'Advanced Content': 4.4,
      'Free Trial': 4.5,
    },
  },
  {
    slug: 'dreamgf-review',
    name: 'DreamGF',
    summary:
      'DreamGF offers a versatile AI girlfriend platform focused on deep customization and engaging chats, giving users plenty of control over their virtual experiences with well-designed characters.',
    lastTested: '2025-08-18',
    categoryScores: {
      'Chat Quality': 4.1,
      'Image Quality': 4.2,
      Features: 4.3,
      'Generative Tools': 4.0,
      'User Experience': 3.8,
      'Value/Money': 3.9,
      Characters: 4.0,
      Customization: 4.3,
      'Advanced Content': 4.1,
      'Free Trial': 2.7,
    },
  },
  {
    slug: 'nextpart-ai-review',
    name: 'Nextpart AI',
    summary:
      'Nextpart AI pairs NSFW roleplay chats with Free Molding character creation and advanced video tools, backed by one of the stronger free credit trials we tested.',
    lastTested: '2026-10-29',
    categoryScores: {
      'Chat Quality': 3.9,
      'Image Quality': 3.8,
      Features: 4.0,
      'Generative Tools': 4.1,
      'User Experience': 3.9,
      'Value/Money': 3.9,
      Characters: 3.2,
      Customization: 4.4,
      'Advanced Content': 4.2,
      'Free Trial': 4.8,
    },
  },
  {
    slug: 'kupid-ai-review',
    name: 'Kupid AI',
    summary:
      'Kupid AI pairs easy swipe-style chat with strong image and video tools in one simple app.',
    lastTested: '2025-07-03',
    categoryScores: {
      'Chat Quality': 4.0,
      'Image Quality': 4.1,
      Features: 4.2,
      'Generative Tools': 4.1,
      'User Experience': 3.8,
      'Value/Money': 3.6,
      Characters: 4.0,
      Customization: 3.9,
      'Advanced Content': 4.0,
      'Free Trial': 3.3,
    },
  },
  {
    slug: 'lovescape-ai-review',
    name: 'Lovescape AI',
    summary:
      'Lovescape AI combines engaging story-consistent chat, realistic voice messages, and easy image and video tools. Character looks and suggested replies still vary in quality.',
    lastTested: '2026-10-31',
    categoryScores: {
      'Chat Quality': 3.7,
      'Image Quality': 4.0,
      Features: 4.2,
      'Generative Tools': 4.1,
      'User Experience': 4.0,
      'Value/Money': 3.6,
      Characters: 3.8,
      Customization: 3.9,
      'Advanced Content': 4.2,
      'Free Trial': 4.0,
    },
  },
  {
    slug: 'luvr-ai-review',
    name: 'Luvr AI',
    summary:
      'Luvr AI combines decent customization with friendly user experience, making it a reliable choice for those wanting a balanced virtual girlfriend app with both chatting and light generative features.',
    lastTested: '2025-07-04',
    categoryScores: {
      'Chat Quality': 4.0,
      'Image Quality': 2.7,
      Features: 4.3,
      'Generative Tools': 2.4,
      'User Experience': 3.9,
      'Value/Money': 3.8,
      Characters: 4.1,
      Customization: 4.2,
      'Advanced Content': 4.9,
      'Free Trial': 4.7,
    },
  },
  {
    slug: 'fantasygf-review',
    name: 'FantasyGF',
    summary:
      'FantasyGF is a complete AI girlfriend experience with strong customization, lively chats, and unique features like AI kiss videos and character interactions that enhance your virtual partner engagement.',
    lastTested: '2026-01-15',
    categoryScores: {
      'Chat Quality': 3.9,
      'Image Quality': 3.6,
      Features: 4.1,
      'Generative Tools': 4.0,
      'User Experience': 3.8,
      'Value/Money': 3.7,
      Characters: 3.3,
      Customization: 3.9,
      'Advanced Content': 4.1,
      'Free Trial': 2.9,
    },
  },
  {
    slug: 'herahaven-ai-review',
    name: 'HeraHaven AI',
    summary:
      'HeraHaven AI delivers strong chat realism and guided scenarios, offering an immersive AI girlfriend experience with appealing characters and expressive, premium-oriented conversations.',
    lastTested: '2025-07-05',
    categoryScores: {
      'Chat Quality': 4.0,
      'Image Quality': 3.3,
      Features: 4.5,
      'Generative Tools': 3.2,
      'User Experience': 4.1,
      'Value/Money': 3.0,
      Characters: 4.3,
      Customization: 2.7,
      'Advanced Content': 4.7,
      'Free Trial': 3.1,
    },
  },
  {
    slug: 'gptgirlfriend-review',
    name: 'GPTGirlfriend',
    summary:
      'GPTGirlfriend is an easy-to-use AI girlfriend chatbot with good image quality and enjoyable conversations, ideal for straightforward virtual companion experiences.',
    lastTested: '2025-06-15',
    categoryScores: {
      'Chat Quality': 4.3,
      'Image Quality': 2.8,
      Features: 3.6,
      'Generative Tools': 2.6,
      'User Experience': 3.7,
      'Value/Money': 3.8,
      Characters: 4.5,
      Customization: 3.4,
      'Advanced Content': 4.4,
      'Free Trial': 3.1,
    },
  },
  {
    slug: 'spicy-chat-ai-review',
    name: 'Spicy Chat AI',
    summary:
      'Spicy Chat AI is a mature-focused AI chat platform with excellent chat quality and characters, designed for users who want fun, mature-oriented conversations with AI companions.',
    lastTested: '2025-05-24',
    categoryScores: {
      'Chat Quality': 4.0,
      'Image Quality': 3.2,
      Features: 3.6,
      'Generative Tools': 2.9,
      'User Experience': 3.4,
      'Value/Money': 3.5,
      Characters: 4.1,
      Customization: 3.7,
      'Advanced Content': 4.2,
      'Free Trial': 3.6,
    },
  },
  {
    slug: 'uncensy-review',
    name: 'Uncensy',
    summary:
      'Uncensy offers adult (18+) AI chat with strong conversation quality that appeals to users seeking adult (18+) or free-form virtual dialogues.',
    lastTested: '2026-01-20',
    categoryScores: {
      'Chat Quality': 4.4,
      'Image Quality': 2.9,
      Features: 2.8,
      'Generative Tools': 2.7,
      'User Experience': 3.6,
      'Value/Money': 2.8,
      Characters: 4.5,
      Customization: 4.3,
      'Advanced Content': 4.5,
      'Free Trial': 2.6,
    },
  },
  {
    slug: 'soulgen-ai-review',
    name: 'SoulGen AI',
    summary:
      'SoulGen AI is an AI girlfriend and creative image platform with exceptional generative tools, great visuals, and engaging chat features for creative romance experiences.',
    lastTested: '2025-07-24',
    categoryScores: {
      'Chat Quality': 3.4,
      'Image Quality': 4.0,
      Features: 3.6,
      'Generative Tools': 4.1,
      'User Experience': 3.5,
      'Value/Money': 3.2,
      Characters: 3.3,
      Customization: 3.6,
      'Advanced Content': 3.1,
      'Free Trial': 2.5,
    },
  },
  {
    slug: 'mylovely-ai-review',
    name: 'MyLovely AI',
    summary:
      'MyLovely.ai is an AI girlfriend platform with story scenarios, in-character chat, custom image prompts, and video made from those images. Chat and prompt match are the strengths. The chat layout and listed ages are not.',
    lastTested: '2026-10-26',
    categoryScores: {
      'Chat Quality': 3.6,
      'Image Quality': 3.5,
      Features: 3.5,
      'Generative Tools': 3.8,
      'User Experience': 2.5,
      'Value/Money': 3.0,
      Characters: 3.4,
      Customization: 3.8,
      'Advanced Content': 4.2,
      'Free Trial': 3.8,
    },
  },
  {
    slug: 'romantic-ai-review',
    name: 'Romantic AI',
    summary:
      'Romantic AI is a web and mobile AI girlfriend platform with custom girlfriend, boyfriend, and anime builders, optional NSFW spicy chat, AI photo generation, and Hearts pricing for images on romanticai.com.',
    lastTested: '2025-06-29',
    categoryScores: {
      'Chat Quality': 3.7,
      'Image Quality': 3.3,
      Features: 3.0,
      'Generative Tools': 3.2,
      'User Experience': 3.5,
      'Value/Money': 3.4,
      Characters: 3.1,
      Customization: 3.8,
      'Advanced Content': 3.3,
      'Free Trial': 3.2,
    },
  },
  {
    slug: 'character-ai-review',
    name: 'Character AI',
    summary:
      "Character AI provides general conversational AI with customizable personas, but it's not focused on mature girlfriend experiences, making it better for friendly roleplay and creative chats.",
    lastTested: '2026-01-31',
    categoryScores: {
      'Chat Quality': 4.7,
      'Image Quality': 2.0,
      Features: 3.1,
      'Generative Tools': 1.1,
      'User Experience': 4.6,
      'Value/Money': 3.3,
      Characters: 4.8,
      Customization: 3.7,
      'Advanced Content': 0.4,
      'Free Trial': 4.7,
    },
  },
  {
    slug: 'charstar-ai-review',
    name: 'Charstar AI',
    summary:
      'Charstar AI offers top-tier chat realism and character quality, appealing to users who want lifelike conversations with diverse AI personalities.',
    lastTested: '2025-08-06',
    categoryScores: {
      'Chat Quality': 4.1,
      'Image Quality': 1.9,
      Features: 3.6,
      'Generative Tools': 1.8,
      'User Experience': 3.4,
      'Value/Money': 3.2,
      Characters: 3.8,
      Customization: 3.7,
      'Advanced Content': 3.9,
      'Free Trial': 3.5,
    },
  },
  {
    slug: 'replika-ai-review',
    name: 'Replika AI',
    summary:
      'Replika AI is a long-standing AI companion chatbot that focuses on emotional connection and attachment, perfect for users seeking supportive, everyday conversational AI.',
    lastTested: '2025-08-13',
    categoryScores: {
      'Chat Quality': 3.9,
      'Image Quality': 3.7,
      Features: 3.3,
      'Generative Tools': 2.5,
      'User Experience': 3.8,
      'Value/Money': 2.2,
      Characters: 3.0,
      Customization: 3.6,
      'Advanced Content': 2.4,
      'Free Trial': 3.5,
    },
  },
  {
    slug: 'nomi-ai-review',
    name: 'Nomi AI',
    summary:
      'An AI girlfriend platform focused on deep, single-companion relationships, combining advanced memory, detailed customization, and immersive roleplay chats for users seeking meaningful virtual connections.',
    lastTested: '2025-07-09',
    categoryScores: {
      'Chat Quality': 3.8,
      'Image Quality': 2.9,
      Features: 3.2,
      'Generative Tools': 2.8,
      'User Experience': 3.0,
      'Value/Money': 3.3,
      Characters: 3.1,
      Customization: 3.2,
      'Advanced Content': 3.0,
      'Free Trial': 2.6,
    },
  },
  {
    slug: 'botify-ai-review',
    name: 'Botify AI',
    summary:
      'An AI chatbot platform designed for beginner-friendly roleplay chats, offering customizable characters and mobile support, but with limited mature features and advanced functionality.',
    lastTested: '2025-08-10',
    categoryScores: {
      'Chat Quality': 2.6,
      'Image Quality': 2.4,
      Features: 2.1,
      'Generative Tools': 2.0,
      'User Experience': 3.8,
      'Value/Money': 3.0,
      Characters: 3.4,
      Customization: 3.1,
      'Advanced Content': 1.9,
      'Free Trial': 3.3,
    },
  },
  {
    slug: 'muah-ai-review',
    name: 'Muah.ai',
    summary:
      'Muah.ai is an adult AI companion platform with strong-looking models, fast flirty chat, voice and calls, but weaker image generation and serious privacy and safety concerns.',
    lastTested: '2026-07-04',
    categoryScores: {
      'Chat Quality': 3.1,
      'Image Quality': 2.1,
      Features: 2.9,
      'Generative Tools': 2.0,
      'User Experience': 2.8,
      'Value/Money': 2.7,
      Characters: 3.0,
      Customization: 2.6,
      'Advanced Content': 2.5,
      'Free Trial': 2.6,
    },
  },
];

function affiliateKey(slug: string): AffiliateSlug {
  const key = getAffiliateSlugFromReviewSlug(slug);
  if (!key) throw new Error(`ranked platform ${slug} has no affiliate slug in lib/affiliateLinks.ts`);
  return key;
}

const RANKED_PLATFORMS: RankedPlatform[] = PLATFORM_RANKING.map(
  ({ categoryScores, ...entry }, index) => {
    const categories = STANDARD_RATING_CATEGORIES.map((name) => ({
      name,
      score: categoryScores[name],
    }));
    return {
      ...entry,
      key: affiliateKey(entry.slug),
      rank: index + 1,
      rating: computeOverallRating(categories),
      categories,
    };
  }
);

const RANKED_BY_SLUG = new Map(RANKED_PLATFORMS.map((platform) => [platform.slug, platform]));

export function getRankedPlatforms(): RankedPlatform[] {
  return RANKED_PLATFORMS;
}

export function getRankedSlugs(): string[] {
  return RANKED_PLATFORMS.map((platform) => platform.slug);
}

export function getPlatform(slug: string): RankedPlatform | null {
  return RANKED_BY_SLUG.get(slug) ?? null;
}

/** 1-based overall rank, or null when the slug is not on the overall list. */
export function getRank(slug: string): number | null {
  return RANKED_BY_SLUG.get(slug)?.rank ?? null;
}

export function getScore(slug: string): number | null {
  return RANKED_BY_SLUG.get(slug)?.rating ?? null;
}

export function getSummary(slug: string): string | null {
  return RANKED_BY_SLUG.get(slug)?.summary ?? null;
}

export function getTopPick(): RankedPlatform {
  return RANKED_PLATFORMS[0];
}

/** "#1", "#2", … or null when unranked. */
export function formatOverallRankLabel(slug: string): string | null {
  const rank = getRank(slug);
  return rank === null ? null : `#${rank}`;
}

export function getRankBadge(slug: string): "Editor's Choice" | 'Expert Pick' | null {
  const rank = getRank(slug);
  if (rank === 1) return "Editor's Choice";
  if (rank === 2 || rank === 3) return 'Expert Pick';
  return null;
}

export function getRankingLastVerifiedCopy() {
  return `Rankings last verified ${RANKING_LAST_VERIFIED_LABEL}.`;
}
